import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { EUROPE_LAND } from './europe-geo';

interface CountryNode {
  /** ISO 3166-1 alpha-2 code (XK for Kosovo) */
  code: string;
  country: string;
  /** Region city for live hubs, capital for the rest */
  city: string;
  lat: number;
  lon: number;
  live: boolean;
}

interface PlottedNode extends CountryNode {
  x: number;
  y: number;
}

@Component({
  selector: 'app-roadmap',
  imports: [MatButtonModule, MatIconModule, RouterLink, TranslateModule],
  templateUrl: './roadmap.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './roadmap.component.css'
})
export class RoadmapComponent {
  /** SVG canvas. Europe is taller than it is wide, so the box matches. */
  readonly mapWidth = 960;
  readonly mapHeight = 599;

  // Equirectangular projection bounds: wide North-Atlantic frame (all of Greenland + Europe).
  private readonly LON_MIN = -58;
  private readonly LON_MAX = 44;
  private readonly LAT_MAX = 72;
  private readonly LAT_MIN = 32;
  // Longitude degrees are compressed by cos(meanLat) so the geography keeps Europe's shape.
  private readonly COS = Math.cos((51 * Math.PI) / 180);
  // No padding: geography fills the viewBox edge-to-edge so the SVG matches its container's rectangle.
  private readonly PAD = 0;

  /**
   * Every sovereign European country. `live` marks regions deployable today;
   * the rest are on the roadmap. Capital coordinates are used for positioning,
   * except live hubs which sit on their actual region city.
   */
  private readonly countries: CountryNode[] = [
    { code: 'DE', country: 'Germany', city: 'Nuremberg', lat: 49.45, lon: 11.08, live: true },
    { code: 'FI', country: 'Finland', city: 'Helsinki', lat: 60.17, lon: 24.94, live: true },
    { code: 'PT', country: 'Portugal', city: 'Lisbon', lat: 38.72, lon: -9.14, live: false },
    { code: 'ES', country: 'Spain', city: 'Madrid', lat: 40.42, lon: -3.7, live: false },
    { code: 'FR', country: 'France', city: 'Paris', lat: 48.85, lon: 2.35, live: false },
    { code: 'IE', country: 'Ireland', city: 'Dublin', lat: 53.35, lon: -6.26, live: false },
    { code: 'GB', country: 'United Kingdom', city: 'London', lat: 51.51, lon: -0.13, live: false },
    { code: 'BE', country: 'Belgium', city: 'Brussels', lat: 50.85, lon: 4.35, live: false },
    { code: 'NL', country: 'Netherlands', city: 'Amsterdam', lat: 52.37, lon: 4.9, live: false },
    { code: 'LU', country: 'Luxembourg', city: 'Luxembourg', lat: 49.61, lon: 6.13, live: false },
    { code: 'CH', country: 'Switzerland', city: 'Bern', lat: 46.95, lon: 7.45, live: false },
    { code: 'IT', country: 'Italy', city: 'Rome', lat: 41.9, lon: 12.5, live: false },
    { code: 'AT', country: 'Austria', city: 'Vienna', lat: 48.21, lon: 16.37, live: false },
    { code: 'DK', country: 'Denmark', city: 'Copenhagen', lat: 55.68, lon: 12.57, live: false },
    { code: 'NO', country: 'Norway', city: 'Oslo', lat: 59.91, lon: 10.75, live: false },
    { code: 'SE', country: 'Sweden', city: 'Stockholm', lat: 59.33, lon: 18.07, live: false },
    { code: 'PL', country: 'Poland', city: 'Warsaw', lat: 52.23, lon: 21.01, live: false },
    { code: 'CZ', country: 'Czechia', city: 'Prague', lat: 50.08, lon: 14.44, live: false },
    { code: 'SK', country: 'Slovakia', city: 'Bratislava', lat: 48.15, lon: 17.11, live: false },
    { code: 'HU', country: 'Hungary', city: 'Budapest', lat: 47.5, lon: 19.04, live: false },
    { code: 'SI', country: 'Slovenia', city: 'Ljubljana', lat: 46.05, lon: 14.51, live: false },
    { code: 'HR', country: 'Croatia', city: 'Zagreb', lat: 45.81, lon: 15.98, live: false },
    { code: 'BA', country: 'Bosnia & Herzegovina', city: 'Sarajevo', lat: 43.86, lon: 18.41, live: false },
    { code: 'RS', country: 'Serbia', city: 'Belgrade', lat: 44.79, lon: 20.45, live: false },
    { code: 'ME', country: 'Montenegro', city: 'Podgorica', lat: 42.44, lon: 19.26, live: false },
    { code: 'AL', country: 'Albania', city: 'Tirana', lat: 41.33, lon: 19.82, live: false },
    { code: 'MK', country: 'North Macedonia', city: 'Skopje', lat: 41.99, lon: 21.43, live: false },
    { code: 'XK', country: 'Kosovo', city: 'Pristina', lat: 42.66, lon: 21.16, live: false },
    { code: 'GR', country: 'Greece', city: 'Athens', lat: 37.98, lon: 23.73, live: false },
    { code: 'BG', country: 'Bulgaria', city: 'Sofia', lat: 42.7, lon: 23.32, live: false },
    { code: 'RO', country: 'Romania', city: 'Bucharest', lat: 44.43, lon: 26.1, live: false },
    { code: 'MD', country: 'Moldova', city: 'Chișinău', lat: 47.01, lon: 28.86, live: false },
    { code: 'UA', country: 'Ukraine', city: 'Kyiv', lat: 50.45, lon: 30.52, live: false },
    { code: 'BY', country: 'Belarus', city: 'Minsk', lat: 53.9, lon: 27.57, live: false },
    { code: 'LT', country: 'Lithuania', city: 'Vilnius', lat: 54.69, lon: 25.28, live: false },
    { code: 'LV', country: 'Latvia', city: 'Riga', lat: 56.95, lon: 24.11, live: false },
    { code: 'EE', country: 'Estonia', city: 'Tallinn', lat: 59.44, lon: 24.75, live: false },
    { code: 'IS', country: 'Iceland', city: 'Reykjavík', lat: 64.15, lon: -21.94, live: false },
    { code: 'MT', country: 'Malta', city: 'Valletta', lat: 35.9, lon: 14.51, live: false },
    { code: 'CY', country: 'Cyprus', city: 'Nicosia', lat: 35.17, lon: 33.36, live: false },
    { code: 'AD', country: 'Andorra', city: 'Andorra la Vella', lat: 42.51, lon: 1.52, live: false },
    { code: 'MC', country: 'Monaco', city: 'Monaco', lat: 43.73, lon: 7.42, live: false },
    { code: 'SM', country: 'San Marino', city: 'San Marino', lat: 43.94, lon: 12.46, live: false },
    { code: 'LI', country: 'Liechtenstein', city: 'Vaduz', lat: 47.14, lon: 9.52, live: false }
  ];

  /** Uniform scale (px per latitude degree) that fits both axes inside the padded canvas. */
  private get scale(): number {
    const availW = this.mapWidth - this.PAD * 2;
    const availH = this.mapHeight - this.PAD * 2;
    const unitsW = (this.LON_MAX - this.LON_MIN) * this.COS;
    const unitsH = this.LAT_MAX - this.LAT_MIN;
    return Math.min(availW / unitsW, availH / unitsH);
  }

  private project(lat: number, lon: number): { x: number; y: number } {
    return {
      x: Math.round((this.PAD + (lon - this.LON_MIN) * this.COS * this.scale) * 10) / 10,
      y: Math.round((this.PAD + (this.LAT_MAX - lat) * this.scale) * 10) / 10
    };
  }

  readonly nodes: PlottedNode[] = this.countries.map(c => ({ ...c, ...this.project(c.lat, c.lon) }));

  readonly liveNodes: PlottedNode[] = this.nodes.filter(n => n.live);
  readonly soonNodes: PlottedNode[] = this.nodes.filter(n => !n.live);

  /** Real Europe country outlines, projected with the same transform as the markers. */
  readonly land = EUROPE_LAND;
  /** ISO codes deployable today — used to highlight live countries on the map. */
  readonly liveCodes = new Set(this.liveNodes.map(n => n.code));

  readonly liveCount = this.liveNodes.length;
  /** Countries targeted — includes territories not plotted on the map. */
  readonly totalCount = 46;

  /** Expansion goals in order — live hubs first, then the next target cities. */
  readonly milestones: { city: string; country: string; live: boolean; i18n?: boolean }[] = [
    { city: 'Nuremberg', country: 'Germany', live: true },
    { city: 'Helsinki', country: 'Finland', live: true },
    { city: 'Paris', country: 'France', live: false },
    { city: 'Lausanne', country: 'Switzerland', live: false },
    { city: 'Barcelona', country: 'Spain', live: false },
    { city: 'home.roadmap.milestones.more', country: '', live: false, i18n: true }
  ];

  /** Gentle curve joining the two live hubs to read as an existing backbone. Fixed at construction. */
  readonly backbonePath: string = this.buildBackbone();

  private buildBackbone(): string {
    if (this.liveNodes.length < 2) return '';
    const [a, b] = this.liveNodes;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2 - 60;
    return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
  }
}
